import React, { useMemo, useRef, useState } from 'react';
import { Button, Modal, useId, Box, GRAY, IconButton } from '@applyboard/ui-components';
import { PlusOutlineIcon, DeleteFillIcon } from '@applyboard/ui-icons';
import styled from '@emotion/styled';
import { useEventTracker } from '@applyboard/ui-utils';
import useInitialProps from '~/hooks/useInitialProps';
import { BackupProgramEntry, BackupPrograms, StoredProgramType, useBackupPrograms } from '~/data';
import BackupProgramSelector from './BackupProgramSelector';
import { fromBackupProgramEntryToStoredProgramType } from './utils';
import { getApplicationStatusMessage } from '~/util/common';
import { ApplicationStatuses } from '~/types';
import { FocusOnMount } from '~/emerald-ui';

export interface SelectItemProgramSelection extends StoredProgramType {
  label: string; // same as program_name
  value: string | number; // same as program_id
}

interface StoredProgramSelection extends SelectItemProgramSelection {
  position: number;
}

type SelectProgramsModalProps = {
  /**
   * Program intake. Format YYYY-MM-DD
   */
  intake: string;
  /**
   * Maximum number of allowed programs on the list.
   * If zero or not present, limit is defaulted to three.
   * @default 3
   */
  limit?: number;
  onClose: () => void;
  onSave: ({ updatedBackupPrograms }: { updatedBackupPrograms: BackupPrograms }) => void;
  programId: number | string;
  schoolId: number | string;
  schoolGroupId: number | string;
  selectedPrograms: Array<BackupProgramEntry>;
  studentId: number | string;
  excludeEslPrograms: boolean;
  applicationStatus: ApplicationStatuses;
  applicationPaid: boolean;
  applicationId: number | string;
};

const SelectProgramsModal = ({
  intake,
  limit = 3,
  onClose,
  onSave,
  programId,
  schoolId,
  schoolGroupId,
  selectedPrograms: initialSelection,
  studentId,
  excludeEslPrograms,
  applicationStatus,
  applicationPaid,
  applicationId,
}: SelectProgramsModalProps) => {
  const modalId = useId();
  const initialProps = useInitialProps();

  const programCount = useMemo(() => {
    let returnValue = limit;
    if (!initialSelection || !Array.isArray(initialSelection) || initialSelection.length <= 0) {
      return returnValue;
    }

    initialSelection.forEach(backupProgram => {
      returnValue = Math.max(returnValue, backupProgram.position);
    });

    return returnValue;
  }, [initialSelection]);

  const [selectedProgram, setSelectedProgram] = useState<Array<StoredProgramSelection>>(() => {
    if (initialSelection && Array.isArray(initialSelection) && initialSelection.length > 0) {
      return initialSelection.map(e => ({
        ...fromBackupProgramEntryToStoredProgramType(e),
        label: e.programName,
        value: e.backupProgramId,
        position: e.position,
      }));
    }

    return [];
  });

  const isDirty = useMemo<boolean>(
    () => checkIsDirty(initialSelection, selectedProgram),
    [selectedProgram, initialSelection],
  );

  const [hasErrors, setHasErrors] = useState<Array<boolean>>([false, false, false]);

  const [numBackupProgramsDropdownRows, setNumBackupProgramsDropdownRows] = useState(
    selectedProgram?.length || 1,
  );

  const { trackEvent } = useEventTracker();

  const handleOnChangeSelectedProgram = (value: SelectItemProgramSelection, position: number) => {
    setHasErrors(hasErrors.map((element, index) => (index === position - 1 ? !value : element)));

    setSelectedProgram(currentValue =>
      currentValue.filter(e => e.position !== position).concat({ ...value, position }),
    );
  };

  const handleRemoveSelectedProgram = (position: number) => {
    const newHasErrors = [...hasErrors];
    newHasErrors.splice(position - 1, 1);
    newHasErrors.push(false);
    setHasErrors(newHasErrors);

    setSelectedProgram(currentValue =>
      currentValue
        .filter(program => program.position !== position)
        .map(program => ({
          ...program,
          position: program.position < position ? program.position : program.position - 1,
        })),
    );
  };

  const handleSave = () => {
    // make sure there are no blank rows - this can happen if a new row is adding and nothing is selected
    // the onChange event never fires and thus handleOnChangeSelectedProgram never runs and we have no record of the row
    // in selectedProgram
    if (
      !!initialProps?.backupProgramEnhancementP1Enabled &&
      numBackupProgramsDropdownRows !== selectedProgram.length &&
      selectedProgram.length > 0
    ) {
      const newHasErrors = hasErrors
        .map((_, idx) => !selectedProgram.find(program => program.position - 1 === idx))
        .fill(false, numBackupProgramsDropdownRows);
      setHasErrors(newHasErrors);
      return;
    }

    const selectedPrograms: BackupPrograms = selectedProgram.map(element => ({
      id:
        typeof element.programId === 'number' ? element.programId : parseInt(element.programId, 10),
      position: element.position,
    }));

    onSave({ updatedBackupPrograms: selectedPrograms });
    trackEvent({
      event: 'Single Application - Backup Programs - Submits Backup Program',
      data: {
        userRole: initialProps.role,
        studentId,
        applicationId,
        applicationStatus: getApplicationStatusMessage({
          paid: applicationPaid,
          status: applicationStatus,
        }),
        applicationStage: initialProps.application?.current_stage,
        programIds: selectedProgram
          .map(element =>
            typeof element.programId === 'number'
              ? element.programId
              : parseInt(element.programId, 10),
          )
          .toString(),
      },
    });
  };

  const { backupPrograms, backupProgramsIsLoading } = useBackupPrograms(applicationId);

  return (
    <Modal
      id={modalId}
      aria-describedby={`${modalId}-dialog-description`}
      footerButtons={() => (
        <FooterButton
          onClose={onClose}
          onSave={handleSave}
          isDirty={isDirty}
          hasError={hasErrors.includes(true)}
          featureFlag={!!initialProps?.backupProgramEnhancementP1Enabled}
        />
      )}
      handleClose={onClose}
      open={true}
      title={initialProps?.backupProgramEnhancementP1Enabled ? 'Add Backup' : 'Add Backups'}
    >
      {!backupProgramsIsLoading ? (
        <div>
          <ModalVerbiage
            modalId={modalId}
            limit={limit}
            featureFlag={!!initialProps?.backupProgramEnhancementP1Enabled}
          />
          <BackupProgramSelectors
            onChangeSelectedProgram={handleOnChangeSelectedProgram}
            onRemoveSelectedProgram={handleRemoveSelectedProgram}
            programCount={programCount}
            limit={limit}
            selectedProgram={selectedProgram}
            hasErrors={hasErrors}
            featureFlag={!!initialProps?.backupProgramEnhancementP1Enabled}
            intake={intake}
            schoolId={schoolId}
            schoolGroupId={schoolGroupId}
            studentId={studentId}
            excludeEslPrograms={excludeEslPrograms}
            programId={programId}
            backupPrograms={backupPrograms}
            numBackupProgramsDropdownRows={numBackupProgramsDropdownRows}
            setNumBackupProgramsDropdownRows={setNumBackupProgramsDropdownRows}
          />
        </div>
      ) : (
        <div>Loading...</div>
      )}
    </Modal>
  );
};

const Paragraph = styled.p`
  font-family: Open Sans;
  font-size: 16px;
  font-weight: ${(props: { fontWeight?: string | number }) => props.fontWeight || '400'};
  line-height: 24px;
  color: #323847;
  margin-bottom: 8px;
`;

const Caption = styled.p`
  font-family: Open Sans;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  color: #606a84;
`;

const PositionMarker = styled.div`
  font-size: 16px;
  color: ${GRAY[5]};
`;

const ChooseAProgram = styled.div`
  font-family: Montserrat;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 1px;
  text-align: left;
  color: ${GRAY[10]};
`;

function BackupProgramSelectors({
  programCount,
  selectedProgram,
  onChangeSelectedProgram,
  onRemoveSelectedProgram,
  limit,
  featureFlag,
  hasErrors,
  intake,
  schoolId,
  schoolGroupId,
  studentId,
  excludeEslPrograms,
  programId,
  backupPrograms,
  numBackupProgramsDropdownRows,
  setNumBackupProgramsDropdownRows,
}: {
  programCount: number;
  limit: number;
  selectedProgram: Array<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  onChangeSelectedProgram: (program: SelectItemProgramSelection, position: number) => void;
  onRemoveSelectedProgram: (position: number) => void;
  featureFlag: boolean;
  hasErrors: Array<boolean>;
  intake: string;
  schoolId: string | number;
  schoolGroupId: number | string;
  studentId: number | string;
  excludeEslPrograms: boolean;
  programId: number | string;
  backupPrograms: BackupProgramEntry[];
  numBackupProgramsDropdownRows: number;
  setNumBackupProgramsDropdownRows: (any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const [justClickedAdd, setJustClickedAdd] = useState(false);
  const [justRemovedRow, setJustRemovedRow] = useState(false);
  const addProgramRef = useRef(null);

  const removeRow = position => {
    onRemoveSelectedProgram(position);

    if (numBackupProgramsDropdownRows > 1) {
      setJustRemovedRow(true);
      setNumBackupProgramsDropdownRows(currValue => currValue - 1);
      addProgramRef.current?.focus();
    }
  };

  if (featureFlag) {
    return (
      <>
        <Box mt={2.5} ml={7}>
          <ChooseAProgram>CHOOSE A PROGRAM</ChooseAProgram>
        </Box>
        {Array(numBackupProgramsDropdownRows)
          .fill(0)
          .map((e, k) => k + 1)
          .map(position => {
            const value = selectedProgram.find(e => e.position === position);

            return (
              // eslint-disable-next-line react/jsx-key
              <Box display="flex" alignItems="center" flexWrap="nowrap" mt={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink="0"
                  mr={2}
                  width={38}
                  height={38}
                  bgcolor={GRAY[21]}
                  borderRadius={4}
                >
                  <PositionMarker>{position}</PositionMarker>
                </Box>
                <Box width="100%">
                  <FocusOnMount focusIf={justClickedAdd} onFocus={() => setJustClickedAdd(false)}>
                    <BackupProgramSelector
                      onChange={onChangeSelectedProgram}
                      position={position}
                      value={value}
                      error={hasErrors[position - 1]}
                      intake={intake}
                      schoolId={schoolId}
                      schoolGroupId={schoolGroupId}
                      studentId={studentId}
                      excludeEslPrograms={excludeEslPrograms}
                      programId={programId}
                      currentProgram={backupPrograms.find(program => program.position === position)}
                      featureFlag={featureFlag}
                    />
                  </FocusOnMount>
                </Box>
                <Box ml={1}>
                  <IconButton
                    onClick={() => removeRow(position)}
                    aria-label={`Remove program ${position}`}
                  >
                    <DeleteFillIcon color="secondary" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        {numBackupProgramsDropdownRows < limit && (
          <Box mt={2} ml={7}>
            <FocusOnMount focusIf={justRemovedRow} onFocus={() => setJustRemovedRow(false)}>
              <Button
                startIcon={<PlusOutlineIcon />}
                variant="text"
                disableRipple={false}
                disableFocusRipple={false}
                onClick={() => {
                  if (numBackupProgramsDropdownRows + 1 <= limit) {
                    setJustClickedAdd(true);
                    setNumBackupProgramsDropdownRows(currValue => currValue + 1);
                  }
                }}
                ref={addProgramRef}
              >
                Add new program
              </Button>
            </FocusOnMount>
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      {Array(programCount)
        .fill(0)
        .map((e, k) => k + 1)
        .map(position => {
          const value = selectedProgram.find(e => e.position === position);

          return (
            // eslint-disable-next-line react/jsx-key
            <Box mt={4}>
              <BackupProgramSelector
                onChange={onChangeSelectedProgram}
                position={position}
                value={value}
                intake={intake}
                schoolId={schoolId}
                schoolGroupId={schoolGroupId}
                studentId={studentId}
                excludeEslPrograms={excludeEslPrograms}
                programId={programId}
                currentProgram={backupPrograms.find(program => program.position === position)}
                featureFlag={featureFlag}
              />
            </Box>
          );
        })}
    </>
  );
}

function FooterButton({
  onClose,
  onSave,
  isDirty,
  featureFlag,
  hasError,
}: {
  onClose: () => void;
  onSave: () => void;
  isDirty: boolean;
  featureFlag: boolean;
  hasError: boolean;
}) {
  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        disableRipple={false}
        disableFocusRipple={false}
        onClick={onClose}
      >
        {featureFlag ? 'Cancel' : 'Never Mind'}
      </Button>
      <Button
        color="primary"
        onClick={onSave}
        disableRipple={false}
        disableFocusRipple={false}
        disabled={featureFlag ? !isDirty || hasError : !isDirty}
      >
        {featureFlag ? 'Save Backup(s)' : 'Select Backup'}
      </Button>
    </>
  );
}

function ModalVerbiage({
  modalId,
  limit,
  featureFlag,
}: {
  modalId: string;
  limit: number;
  featureFlag: boolean;
}) {
  if (featureFlag) {
    return (
      <>
        <Paragraph fontWeight={700}>
          Select up to {limit} Backup Programs in order of preferance.
        </Paragraph>
        <Paragraph id={`${modalId}-dialog-description`}>
          These programs will either be submitted under the same Application Fee for your selected
          program OR will be used in the event your primary choice is not avaliable.
        </Paragraph>
        <Caption>
          Only programs that you are eligble for from the same school can be selected as Backup
          programs.
        </Caption>
      </>
    );
  }

  return (
    <>
      <Paragraph id={`${modalId}-dialog-description`}>
        Select up to {limit} backup programs to be used in the event that the primary program of
        choice is not available or the school offers more than one program selection per
        application.
      </Paragraph>
      <Caption>
        Only eligible programs from the same school and intake can be used as backup.
      </Caption>
    </>
  );
}

export function checkIsDirty(
  initialSelection?: Array<{ position: number; backupProgramId: string | number }>,
  selectedPrograms?: Array<{ position: number; value: string | number }>,
): boolean {
  if (!initialSelection && !selectedPrograms) {
    return false;
  }
  const initialLength = Array.isArray(initialSelection) ? initialSelection.length : 0;
  if (selectedPrograms.length !== initialLength) {
    return true;
  }
  if (selectedPrograms.length === 0) {
    return false;
  }
  return !selectedPrograms.every(value => {
    const initialElement = initialSelection.find(e => e.position === value.position);
    if (!initialElement) {
      return false;
    }

    return Number(initialElement.backupProgramId) === Number(value.value);
  });
}

export default SelectProgramsModal;
